import type {
  AppState,
  DailyWeatherSnapshot,
  DifficultyPreference218,
  EconomicArticle,
  GradeBand218,
  LearningProfile218,
  MissionPreference218,
  PersonalizationInterest218,
  PersonalizationProfile218,
  RecommendationDecision218,
} from './models';

export const personalizationInterestLabels218: Record<PersonalizationInterest218, string> = {
  allowance:'용돈과 소비', saving:'저축과 은행', investment:'투자와 기업', income:'직업과 소득',
  public:'세금과 정부', environment:'환경과 에너지', technology:'기술과 AI', global:'세계경제', startup:'창업과 비즈니스',
};

export const gradeBandLabels218: Record<GradeBand218, string> = {
  elementary:'초등 고학년', middle:'중학생', high:'고등학생',
};

export const missionPreferenceLabels218: Record<MissionPreference218, string> = {
  observe:'관찰', record:'기록', photo:'사진', family:'가족 대화',
};

const interestConcepts: Record<PersonalizationInterest218, string[]> = {
  allowance:['concept_price','concept_choice'], saving:['concept_saving','concept_interest','concept_bank'],
  investment:['concept_investment','concept_company','concept_stock'], income:['concept_income','concept_job','concept_wage'],
  public:['concept_tax','concept_public','concept_policy'], environment:['concept_energy','concept_environment','concept_cost'],
  technology:['concept_ai','concept_technology','concept_digital'], global:['concept_exchange','concept_trade','concept_global'],
  startup:['concept_business','concept_company','concept_profit'],
};

const interestPatterns: Record<PersonalizationInterest218, RegExp> = {
  allowance:/용돈|소비|가격|할인|장보기|선택/,
  saving:/저축|은행|이자|예금/,
  investment:/투자|주식|기업|증권/,
  income:/직업|소득|월급|임금|노동/,
  public:/세금|정부|공공|정책|복지/,
  environment:/환경|에너지|전기|기후|탄소|날씨/,
  technology:/기술|AI|인공지능|디지털|플랫폼/,
  global:/세계|환율|무역|수출|수입|해외/,
  startup:/창업|사업|매출|이익|고객/,
};

export interface PersonalizedArticleView218 {
  difficulty: 1 | 2 | 3;
  difficultyLabel: '가볍게' | '기본' | '깊이 있게';
  title: string;
  summary: string;
  keyPoints: string[];
  missionPrompt: string;
}

export interface RecommendationResult218 {
  decision: RecommendationDecision218;
  article: EconomicArticle;
  alternatives: EconomicArticle[];
  view: PersonalizedArticleView218;
}

export function defaultPersonalizationProfile218(childId: string, date = new Date()): PersonalizationProfile218 {
  return {
    childId,
    gradeBand:'elementary',
    interests:['allowance','saving','technology'],
    regionCode:'서울특별시',
    preferredDuration:3,
    missionPreferences:['observe','record','family'],
    familyMissionEnabled:true,
    difficultyPreference:'auto',
    updatedAt:date.toISOString(),
  };
}

function uniqueAllowed<T extends string>(values: unknown, allowed: readonly T[], fallback: T[]): T[] {
  if (!Array.isArray(values)) return fallback;
  const filtered=values.filter((value):value is T=>typeof value==='string'&&allowed.includes(value as T));
  return [...new Set(filtered)].slice(0,3).length ? [...new Set(filtered)].slice(0,3) : fallback;
}

export function normalizePersonalizationProfile218(input: Partial<PersonalizationProfile218> | undefined, childId: string, date = new Date()): PersonalizationProfile218 {
  const base=defaultPersonalizationProfile218(childId,date);
  const gradeBands:GradeBand218[]=['elementary','middle','high'];
  const interests=Object.keys(personalizationInterestLabels218) as PersonalizationInterest218[];
  const missionPreferences=Object.keys(missionPreferenceLabels218) as MissionPreference218[];
  const duration=[1,3,5].includes(Number(input?.preferredDuration)) ? Number(input?.preferredDuration) as 1|3|5 : base.preferredDuration;
  const difficulty:DifficultyPreference218 = input?.difficultyPreference==='auto'||[1,2,3].includes(Number(input?.difficultyPreference)) ? input?.difficultyPreference as DifficultyPreference218 : 'auto';
  return {
    ...base,
    ...input,
    childId,
    gradeBand:gradeBands.includes(input?.gradeBand as GradeBand218) ? input?.gradeBand as GradeBand218 : base.gradeBand,
    interests:uniqueAllowed(input?.interests,interests,base.interests),
    regionCode:String(input?.regionCode||base.regionCode).slice(0,40),
    preferredDuration:duration,
    missionPreferences:uniqueAllowed(input?.missionPreferences,missionPreferences,base.missionPreferences),
    familyMissionEnabled:input?.familyMissionEnabled!==false,
    difficultyPreference:difficulty,
    updatedAt:input?.updatedAt||base.updatedAt,
  };
}

export function buildLearningProfile218(state: Pick<AppState,'child'|'quizAttempts'|'missions'|'economicArticles'>, date = new Date()): LearningProfile218 {
  const recent=[...state.quizAttempts].sort((a,b)=>String(b.completedAt).localeCompare(String(a.completedAt))).slice(0,10);
  const recentAccuracy=recent.length ? Math.round(recent.reduce((sum,item)=>sum+item.score,0)/recent.length) : 70;
  const decided=state.missions.filter((item)=>['submitted','approved'].includes(item.status));
  const missionCompletionRate=state.missions.length ? Math.round(decided.length/state.missions.length*100) : 50;
  const conceptScores:Record<string,{sum:number;count:number}>={};
  for(const attempt of recent){
    const article=state.economicArticles.find((item)=>item.id===attempt.articleId);
    for(const conceptId of article?.conceptIds??[]){
      const current=conceptScores[conceptId]??{sum:0,count:0};
      current.sum+=attempt.score; current.count+=1; conceptScores[conceptId]=current;
    }
  }
  const conceptMastery=Object.fromEntries(Object.entries(conceptScores).map(([key,value])=>[key,Math.round(value.sum/value.count)]));
  const recommendedDifficulty:1|2|3 = recentAccuracy>=85&&missionCompletionRate>=60 ? 3 : recentAccuracy<60 ? 1 : 2;
  return {
    childId:state.child.id,
    recommendedDifficulty,
    recentAccuracy,
    missionCompletionRate,
    conceptMastery,
    preferredContentTypes:missionCompletionRate>=70?['기사','생활 미션','가족 대화']:['짧은 기사','객관식 퀴즈'],
    calculatedAt:date.toISOString(),
  };
}

function articleInterests(article:EconomicArticle):PersonalizationInterest218[]{
  const text=`${article.title} ${article.summary} ${article.childSummary}`;
  return (Object.keys(personalizationInterestLabels218) as PersonalizationInterest218[]).filter((interest)=>
    article.conceptIds.some((concept)=>interestConcepts[interest].includes(concept))||interestPatterns[interest].test(text)
  );
}

function weatherRelated(article:EconomicArticle,weather:DailyWeatherSnapshot):boolean{
  const text=`${article.title} ${article.summary} ${article.childSummary}`;
  if(['rain','snow','heat','cold','wind','dust'].includes(weather.kind)) return /날씨|기후|농산물|에너지|전기|보험|환경|가격/.test(text);
  return /소비|여가|선택|가격/.test(text);
}

function freshnessScore(article:EconomicArticle,date:Date):number{
  const time=new Date(article.sourcePublishedAt||article.publishedAt||article.updatedAt).getTime();
  if(!Number.isFinite(time))return 4;
  const age=Math.max(0,(date.getTime()-time)/86_400_000);
  return age<=7?10:age<=30?7:age<=90?4:2;
}

function chosenDifficulty(profile:PersonalizationProfile218,learning:LearningProfile218):1|2|3{
  if(profile.difficultyPreference!=='auto') return profile.difficultyPreference;
  const gradeBase:Record<GradeBand218,1|2|3>={elementary:1,middle:2,high:3};
  const combined=Math.round((gradeBase[profile.gradeBand]+learning.recommendedDifficulty)/2);
  return Math.max(1,Math.min(3,combined)) as 1|2|3;
}

export function buildPersonalizedArticleView218(article:EconomicArticle,difficulty:1|2|3,profile:PersonalizationProfile218):PersonalizedArticleView218{
  const labels={1:'가볍게',2:'기본',3:'깊이 있게'} as const;
  const keyPoints=article.keyPoints.length?article.keyPoints:[article.childSummary];
  if(difficulty===1) return {
    difficulty,difficultyLabel:labels[difficulty],title:`쉽게 살펴보기 · ${article.title}`,
    summary:article.childSummary,
    keyPoints:keyPoints.slice(0,2),
    missionPrompt:profile.missionPreferences.includes('observe')?'주변에서 이 기사와 비슷한 가격 변화를 한 가지 찾아보세요.':'기사의 핵심을 한 문장으로 적어보세요.',
  };
  if(difficulty===3) return {
    difficulty,difficultyLabel:labels[difficulty],title:`깊이 탐험하기 · ${article.title}`,
    summary:article.summary||article.childSummary,
    keyPoints:keyPoints.slice(0,3),
    missionPrompt:profile.familyMissionEnabled?'가족과 원인·영향·대응 방법을 하나씩 나누어 이야기해 보세요.':'이 변화가 가계와 시장에 미칠 영향을 두 가지 적어보세요.',
  };
  return {
    difficulty,difficultyLabel:labels[difficulty],title:article.title,summary:article.childSummary,keyPoints:keyPoints.slice(0,3),
    missionPrompt:profile.missionPreferences.includes('record')?'기사 속 원인과 결과를 한 줄로 연결해 보세요.':'오늘 생활에서 적용할 수 있는 선택을 하나 찾아보세요.',
  };
}

export function recommendArticle218(
  state: Pick<AppState,'child'|'economicArticles'|'quizAttempts'|'missions'|'personalizationProfile'|'recommendationHistory'>,
  weather:DailyWeatherSnapshot,
  date=new Date(),
  extraArticles:EconomicArticle[]=[]
):RecommendationResult218|undefined{
  const profile=normalizePersonalizationProfile218(state.personalizationProfile,state.child.id,date);
  const learning=buildLearningProfile218(state,date);
  const difficulty=chosenDifficulty(profile,learning);
  const recentIds=new Set(state.recommendationHistory.slice(0,10).map((item)=>item.contentId));
  const candidates=[...extraArticles,...state.economicArticles]
    .filter((article,index,items)=>items.findIndex((item)=>item.id===article.id)===index)
    .filter((article)=>['published','review'].includes(article.status));
  if(!candidates.length)return undefined;
  const scored=candidates.map((article)=>{
    const topics=articleInterests(article);
    const interestMatch=topics.some((topic)=>profile.interests.includes(topic));
    const interestScore=interestMatch?30:9;
    const difficultyScore=Math.max(5,25-Math.abs(article.difficulty-difficulty)*10);
    const discoveryScore=recentIds.has(article.id)?2:20;
    const weatherScore=weatherRelated(article,weather)?15:4;
    const score=interestScore+difficultyScore+discoveryScore+weatherScore+freshnessScore(article,date);
    const reasons:string[]=[];
    if(interestMatch)reasons.push(`${topics.filter((topic)=>profile.interests.includes(topic)).map((topic)=>personalizationInterestLabels218[topic]).slice(0,2).join('·')} 관심과 연결돼요.`);
    if(weatherRelated(article,weather))reasons.push(`오늘 ${weather.label} 날씨와 연결해 생각할 수 있어요.`);
    reasons.push(`${gradeBandLabels218[profile.gradeBand]}에게 맞는 ${buildPersonalizedArticleView218(article,difficulty,profile).difficultyLabel} 설명으로 보여줘요.`);
    if(!recentIds.has(article.id))reasons.push('최근에 보지 않은 새로운 탐험이에요.');
    return{article,score,reasons};
  }).sort((a,b)=>b.score-a.score||a.article.id.localeCompare(b.article.id));
  const selected=scored[0];
  const alternatives=scored.slice(1).sort((a,b)=>{
    const aNew=articleInterests(a.article).some((topic)=>!profile.interests.includes(topic))?1:0;
    const bNew=articleInterests(b.article).some((topic)=>!profile.interests.includes(topic))?1:0;
    return bNew-aNew||b.score-a.score;
  }).slice(0,2).map((item)=>item.article);
  const createdAt=date.toISOString();
  const decision:RecommendationDecision218={
    id:`recommendation_${date.toISOString().slice(0,10)}_${selected.article.id}_${difficulty}`,
    childId:state.child.id,contentId:selected.article.id,score:selected.score,reasons:selected.reasons.slice(0,3),difficulty,
    alternativeContentIds:alternatives.map((item)=>item.id),createdAt,
  };
  return{decision,article:selected.article,alternatives,view:buildPersonalizedArticleView218(selected.article,difficulty,profile)};
}

export function appendRecommendationHistory218(history:RecommendationDecision218[],decision:RecommendationDecision218):RecommendationDecision218[]{
  if(history.some((item)=>item.id===decision.id))return history;
  return[decision,...history].slice(0,50);
}
